import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Paper,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmergencyOutlined as EmergencyOutlinedIcon,
  PermMediaOutlined as PermMediaOutlinedIcon,
  PolylineOutlined as PolylineOutlinedIcon,
  IntegrationInstructionsOutlined as IntegrationInstructionsOutlinedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PacsMappingDialog from './PacsMappingDialog';
import FirstReaderMappingDialog from './FirstReaderMappingDialog';
import HL7SendNodes from '../HL7SendNodes';
import CDASendNodes from '../CDASendNodes';

const Hl7Settings = ({ 
  hl7AccordionExpanded,
  handleHl7AccordionChange,
  hl7Mappings,
  openMappingDialog,
  deleteMapping,
  loadHl7Mappings
}) => {
  const { t } = useTranslation();
  const [pacsDialogOpen, setPacsDialogOpen] = useState(false);
  const [editingPacsMapping, setEditingPacsMapping] = useState(null);
  const [firstReaderDialogOpen, setFirstReaderDialogOpen] = useState(false);
  const [editingFirstReaderMapping, setEditingFirstReaderMapping] = useState(null);
  const [cdaNodesCount, setCdaNodesCount] = useState(0);
  const [cdaEnabled, setCdaEnabled] = useState(false);

  // Lade CDA-Status beim Mount
  useEffect(() => {
    const fetchCdaStatus = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/cda-send-nodes/active`);
        if (response.ok) {
          const nodes = await response.json();
          setCdaNodesCount(nodes.length);
          setCdaEnabled(nodes.length > 0);
        }
      } catch (error) {
        console.error('Error fetching CDA nodes:', error);
      }
    };
    fetchCdaStatus();
  }, []);

  // Callback wenn CDA-Nodes geändert werden
  const handleCdaNodesChanged = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/cda-send-nodes/active`);
      if (response.ok) {
        const nodes = await response.json();
        setCdaNodesCount(nodes.length);
        setCdaEnabled(nodes.length > 0);
      }
    } catch (error) {
      console.error('Error fetching CDA nodes:', error);
    }
  };

  // Get mappings by source system
  const getMappingsBySource = (sourceSystem) => {
    return hl7Mappings.filter(m => m.source_system === sourceSystem);
  };

  // Get First Reader mappings (RIS mappings with first_reader_field)
  const getFirstReaderMappings = () => {
    return hl7Mappings.filter(m => m.source_system === 'ris' && m.first_reader_field);
  };

  // Get RIS/KIS mappings (RIS mappings ohne first_reader_field)
  const getRisKisMappings = () => {
    return hl7Mappings.filter(
      m =>
        m.source_system === 'ris' &&
        (!m.first_reader_field || String(m.first_reader_field).trim() === '' || String(m.first_reader_field).toLowerCase() === 'null')
    );
  };

  const handlePacsMappingOpen = (mapping = null) => {
    setEditingPacsMapping(mapping);
    setPacsDialogOpen(true);
  };

  const handlePacsMappingClose = () => {
    setPacsDialogOpen(false);
    setEditingPacsMapping(null);
  };

  const handlePacsMappingSave = () => {
    // Refresh mappings after save
    if (loadHl7Mappings) {
      loadHl7Mappings();
    }
  };

  const handleFirstReaderMappingOpen = (mapping = null) => {
    setEditingFirstReaderMapping(mapping);
    setFirstReaderDialogOpen(true);
  };

  const handleFirstReaderMappingClose = () => {
    setFirstReaderDialogOpen(false);
    setEditingFirstReaderMapping(null);
  };

  const handleFirstReaderMappingSave = () => {
    // Refresh mappings after save
    if (loadHl7Mappings) {
      loadHl7Mappings();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Hauptüberschrift */}
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IntegrationInstructionsOutlinedIcon color="primary" />
        {t('admin.serverManagement.hl7Settings')}
      </Typography>

      {/* HL7 RIS/KIS Mapping */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <EmergencyOutlinedIcon sx={{ color: 'var(--rd-primary)' }} />
          <Typography variant="h6">
            HL7 RIS/KIS Mapping
          </Typography>
        </Box>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('admin.hl7Mapping.risKisDescription')}
        </Typography>
        
        {/* Existing Mappings */}
        <Box sx={{ mt: 2 }}>
          {getRisKisMappings().map((mapping) => (
            <Card key={mapping.id} sx={{ mb: '20px', mr: '20px', display: 'inline-block', width: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {mapping.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {mapping.description}
                </Typography>
                <Chip 
                  label={mapping.is_active ? t('admin.hl7Mapping.active') : t('admin.hl7Mapping.inactive')}
                  size="small"
                  color={mapping.is_active ? 'success' : 'default'}
                />
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => openMappingDialog(mapping)}
                >
                  {t('common.edit')}
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteMapping(mapping.id)}
                >
                  {t('common.delete')}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openMappingDialog(null, 'ris')}
            className="button"
          >
            {t('admin.hl7Mapping.addMapping')}
          </Button>
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          {t('admin.hl7Mapping.activeMappings')}: {getRisKisMappings().filter(m => m.is_active).length}
        </Typography>
      </Paper>

      {/* HL7 PACS Mapping */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PermMediaOutlinedIcon sx={{ color: 'var(--rd-primary)' }} />
          <Typography variant="h6">
            HL7 PACS Mapping
          </Typography>
        </Box>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('admin.hl7Mapping.pacsDescription')}
        </Typography>
        
        {/* Existing Mappings */}
        <Box sx={{ mt: 2 }}>
          {getMappingsBySource('pacs').map((mapping) => (
            <Card key={mapping.id} sx={{ mb: '20px', mr: '20px', display: 'inline-block', width: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {mapping.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {mapping.description}
                </Typography>
                <Chip 
                  label={mapping.is_active ? t('admin.hl7Mapping.active') : t('admin.hl7Mapping.inactive')}
                  size="small"
                  color={mapping.is_active ? 'success' : 'default'}
                />
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handlePacsMappingOpen(mapping)}
                >
                  {t('common.edit')}
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteMapping(mapping.id)}
                >
                  {t('common.delete')}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handlePacsMappingOpen(null)}
            className="button"
          >
            {t('admin.hl7Mapping.addMapping')}
          </Button>
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          {t('admin.hl7Mapping.activeMappings')}: {getMappingsBySource('pacs').filter(m => m.is_active).length}
        </Typography>
      </Paper>

      {/* First Reader Mapping */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <EmergencyOutlinedIcon sx={{ color: 'var(--rd-primary)' }} />
          <Typography variant="h6">
            {t('admin.hl7Mapping.firstReaderMappingTitle')}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('admin.hl7Mapping.firstReaderDescription')}
        </Typography>
        
        {/* Existing Mappings */}
        <Box sx={{ mt: 2 }}>
          {getFirstReaderMappings().map((mapping) => (
            <Card key={mapping.id} sx={{ mb: '20px', mr: '20px', display: 'inline-block', width: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {mapping.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {mapping.description}
                </Typography>
                <Chip 
                  label={mapping.is_active ? t('admin.hl7Mapping.active') : t('admin.hl7Mapping.inactive')}
                  size="small"
                  color={mapping.is_active ? 'success' : 'default'}
                />
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleFirstReaderMappingOpen(mapping)}
                >
                  {t('common.edit')}
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteMapping(mapping.id)}
                >
                  {t('common.delete')}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleFirstReaderMappingOpen(null)}
            className="button"
          >
            {t('admin.hl7Mapping.addMapping')}
          </Button>
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          {t('admin.hl7Mapping.activeMappings')}: {getFirstReaderMappings().filter(m => m.is_active).length}
        </Typography>
      </Paper>

      {/* HL7 Sendeknoten */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PolylineOutlinedIcon sx={{ color: 'orange' }} />
          <Typography variant="h6">
            {t('admin.hl7Mapping.sendNodes.title', 'HL7 Sendeknoten')}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('admin.hl7Mapping.sendNodes.description', 'Konfigurieren Sie die HL7 Sendeknoten, an die Befunde übertragen werden.')}
        </Typography>
        
        <HL7SendNodes />
      </Paper>

      {/* CDA Sendeknoten (für ELGA) */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PolylineOutlinedIcon sx={{ color: 'var(--rd-info)' }} />
            <Typography variant="h6">
              {t('admin.cdaNodes.title', 'CDA Sendeknoten (ELGA)')}
            </Typography>
          </Box>
          <Chip 
            label={cdaEnabled 
              ? t('admin.cdaNodes.statusActive', 'CDA aktiv') 
              : t('admin.cdaNodes.statusInactive', 'CDA inaktiv')
            }
            color={cdaEnabled ? 'success' : 'default'}
            size="medium"
          />
        </Box>
        
        {cdaEnabled ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('admin.cdaNodes.activeInfo', 'CDA-Generierung ist aktiv. Nach erfolgreicher HL7-Übertragung werden CDA-Dokumente automatisch an {{count}} konfigurierte Knoten gesendet.', { count: cdaNodesCount })}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('admin.cdaNodes.inactiveInfo', 'CDA-Generierung ist deaktiviert. Fügen Sie mindestens einen aktiven CDA-Sendeknoten hinzu, um CDA-Dokumente für ELGA zu generieren.')}
          </Alert>
        )}
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t('admin.cdaNodes.description', 'CDA-Sendeknoten für österreichische ELGA-Integration. CDA-Dokumente werden an das PACS gesendet, welches sie zur ELGA hochlädt.')}
        </Typography>
        
        <CDASendNodes onNodesChanged={handleCdaNodesChanged} />
      </Paper>

      {/* PACS Mapping Dialog */}
      <PacsMappingDialog
        open={pacsDialogOpen}
        onClose={handlePacsMappingClose}
        mapping={editingPacsMapping}
        onSave={handlePacsMappingSave}
      />

      {/* First Reader Mapping Dialog */}
      <FirstReaderMappingDialog
        open={firstReaderDialogOpen}
        onClose={handleFirstReaderMappingClose}
        mapping={editingFirstReaderMapping}
        onSave={handleFirstReaderMappingSave}
      />
    </Box>
  );
};

export default Hl7Settings;
